import React, { useEffect, useState } from 'react';
import { Alert, Button, Space } from 'antd';
import { ClockCircleOutlined, WarningOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';

const TrialBanner = ({ daysRemaining, onPurchaseClick }) => {
  const [bannerBottom, setBannerBottom] = useState(0);
  const { t } = useTranslation();
  
  console.log('🎨 TRIAL BANNER COMPONENT: Rendering with daysRemaining:', daysRemaining);
  
  // Don't show banner if no trial info or if trial has expired (negative days)
  if (daysRemaining === null || daysRemaining === undefined || daysRemaining < 0) {
    console.log('⚠️ TRIAL BANNER COMPONENT: Not rendering - invalid daysRemaining:', daysRemaining);
    return null;
  }
  
  console.log('✅ TRIAL BANNER COMPONENT: Will render banner');
  
  useEffect(() => {
    const checkFooterPosition = () => {
      // Try to find the footer element
      const footer = document.querySelector('footer') || 
                     document.querySelector('.footer') || 
                     document.querySelector('[class*="footer"]');
      
      if (footer) {
        const footerRect = footer.getBoundingClientRect();
        const windowHeight = window.innerHeight;
        
        // If footer is visible in viewport
        if (footerRect.top < windowHeight) {
          // Position banner above footer
          const newBottom = Math.max(0, windowHeight - footerRect.top);
          setBannerBottom(newBottom);
        } else {
          // Footer not visible, stick to bottom
          setBannerBottom(0);
        }
      }
    };
    
    // Check on mount, scroll, and resize
    checkFooterPosition();
    window.addEventListener('scroll', checkFooterPosition);
    window.addEventListener('resize', checkFooterPosition);
    
    return () => {
      window.removeEventListener('scroll', checkFooterPosition);
      window.removeEventListener('resize', checkFooterPosition);
    };
  }, []);

  // Determine banner type and message based on days remaining
  let alertType = 'info';
  let icon = <ClockCircleOutlined />;
  let message = '';
  let description = '';
  let showPurchaseButton = false;

  if (daysRemaining > 1) {
    alertType = 'info';
    icon = <ClockCircleOutlined />;
    message = t('trialBanner.daysRemaining', { count: daysRemaining });
    description = t('trialBanner.active');
    showPurchaseButton = daysRemaining <= 7;
  } else if (daysRemaining === 1) {
    alertType = 'warning';
    icon = <WarningOutlined />;
    message = t('trialBanner.expiresTomorrow');
    description = t('trialBanner.addLicenses');
    showPurchaseButton = true;
  } else if (daysRemaining === 0) {
    alertType = 'error';
    icon = <ExclamationCircleOutlined />;
    message = t('trialBanner.expiresToday');
    description = t('trialBanner.lastDay');
    showPurchaseButton = true;
  }

  const handlePurchaseClick = () => {
    // In shadow mode, just log the action
    console.log('🛒 SHADOW MODE: User clicked purchase licenses button');
    console.log('🔗 Would redirect to IzzyGo AppSource listing');
    
    if (onPurchaseClick) {
      onPurchaseClick();
    } else {
      // Open IzzyGo AppSource listing for purchase
      window.open('https://appsource.microsoft.com/en-us/marketplace/checkout/appdate1586417772427.izzygo?tab=Overview', '_blank');
    }
  };

  return (
    <>
      {/* Spacer div to push content up and prevent overlap */}
      <div style={{ height: '60px' }} />
      
      {/* Actual banner */}
      <div style={{ 
        position: 'fixed',
        bottom: `${bannerBottom}px`,
        left: 0,
        right: 0,
        width: '100%',
        padding: '8px 16px',
        backgroundColor: '#f8f9fa',
        borderTop: '2px solid #e9ecef',
        boxShadow: '0 -2px 10px rgba(0, 0, 0, 0.1)',
        zIndex: 100, // Lower z-index to not interfere with modals
        transition: 'bottom 0.3s ease' // Smooth transition when adjusting position
      }}>
      <Alert
        message={
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%' }}>
            <Space size="large">
              <Space>
                <span style={{ fontWeight: 500 }}>{message}</span>
                <span style={{ opacity: 0.8, marginLeft: '8px' }}>{description}</span>
              </Space>
              {showPurchaseButton && (
                <Button 
                  type="primary" 
                  size="small"
                  onClick={handlePurchaseClick}
                  style={{ 
                    minWidth: '120px',
                    background: alertType === 'error' ? '#ff4d4f' : undefined 
                  }}
                >
                  {t('common.purchaseLicenses')}
                </Button>
              )}
            </Space>
          </div>
        }
        type={alertType}
        banner
        closable={false}
        style={{ 
          margin: 0,
          border: 'none',
          borderRadius: 0,
          padding: '4px 0'
        }}
      />
    </div>
    </>
  );
};

export default TrialBanner;